import { motion, useScroll, useTransform } from 'framer-motion';
import { useI18n } from '@/lib/i18n';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Link } from 'react-router-dom';
import { ArrowUpRight } from 'lucide-react';
import { useRef } from 'react';
import placeholderImg from '@/assets/placeholder-project.jpg';
import positiveTechImg from '@/assets/cases/positive-technologies.jpg';
import megafonImg from '@/assets/cases/megafon-pre5g.jpg';
import futureGamesImg from '@/assets/cases/future-games.jpg';

const placeholders = [
  { titleRu: 'Positive Technologies', titleEn: 'Positive Technologies', descRu: 'Зрелищное и технологичное шоу, демонстрирующее инновационность нового решения.', descEn: 'A spectacular and technological show demonstrating the innovation of a new solution.', image: positiveTechImg },
  { titleRu: 'Megafon - Pre-5G', titleEn: 'Megafon - Pre-5G', descRu: 'Рекламный ролик с Александром Петровым, приуроченный к открытию доступа к сети 5G.', descEn: 'A commercial featuring Alexander Petrov, dedicated to the launch of 5G network access.', image: megafonImg },
  { titleRu: 'Future Games', titleEn: 'Future Games', descRu: 'Международный мультиспортивный фиджитал-турнир', descEn: 'International multisport phygital tournament', image: futureGamesImg },
];

const PlaceholderCard = ({ item, index }: { item: typeof placeholders[0]; index: number }) => {
  const { lang, t } = useI18n();
  const ref = useRef<HTMLAnchorElement>(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ['start end', 'end start'],
  });
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.3]);

  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ delay: index * 0.1 }}
    >
      <Link to="/case/placeholder" ref={ref} className="block relative w-full aspect-[16/9] rounded-2xl overflow-hidden group cursor-pointer">
        <motion.img
          src={item.image}
          alt="Project placeholder"
          style={{ scale }}
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-background/40 group-hover:bg-background/80 transition-colors duration-500" />
        <div className="absolute inset-0 flex items-center justify-center text-center p-6">
          <div>
            <h3 className="text-2xl md:text-4xl font-display font-bold transition-colors duration-500 group-hover:text-primary">
              {lang === 'ru' ? item.titleRu : item.titleEn}
            </h3>
            <div className="max-h-0 opacity-0 overflow-hidden transition-all duration-700 ease-out group-hover:max-h-40 group-hover:opacity-100">
              <p className="text-muted-foreground text-sm md:text-base leading-relaxed max-w-xl mt-2">
                {lang === 'ru' ? item.descRu : item.descEn}
              </p>
            </div>
          </div>
        </div>
      </Link>
    </motion.div>
  );
};

const PortfolioGrid = () => {
  const { lang, t } = useI18n();

  const { data: settings } = useQuery({
    queryKey: ['site-settings'],
    queryFn: async () => {
      const { data } = await supabase.from('site_settings').select('*').maybeSingle();
      return data;
    },
  });

  const { data: cases, isLoading } = useQuery({
    queryKey: ['cases-public'],
    queryFn: async () => {
      const { data } = await supabase
        .from('cases')
        .select('*')
        .eq('is_published', true)
        .order('sort_order')
        .limit(3);
      return data || [];
    },
  });

  const title = lang === 'ru'
    ? (settings?.portfolio_title_ru) || 'Избранные проекты'
    : (settings?.portfolio_title_en) || 'Selected works';

  return (
    <section id="portfolio" className="py-24 md:py-32">
      <div className="container">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="mb-16 text-center"
        >
          <h2 className="text-3xl md:text-5xl font-display font-bold">
            {title}
          </h2>
        </motion.div>

        {isLoading ? (
          <div className="flex flex-col gap-6">
            {[1, 2, 3].map((i) => (
              <div key={i} className="w-full aspect-[16/9] rounded-2xl bg-secondary animate-pulse" />
            ))}
          </div>
        ) : cases && cases.length > 0 ? (
          <>
            <div className="flex flex-col gap-6">
              {cases.map((item, i) => {
                const title = lang === 'ru' ? item.title_ru : item.title_en;
                const desc = lang === 'ru' ? item.description_ru : item.description_en;
                return (
                  <motion.div
                    key={item.id}
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: i * 0.1 }}
                  >
                    <Link to={`/case/${item.slug}`} className="block relative w-full aspect-[16/9] rounded-2xl overflow-hidden group cursor-pointer">
                      {item.thumbnail_url ? (
                        <img src={item.thumbnail_url} alt={title} className="w-full h-full object-cover" />
                      ) : (
                        <div className="w-full h-full bg-secondary" />
                      )}
                      <div className="absolute inset-0 bg-background/40 group-hover:bg-background/80 transition-colors duration-500" />
                      <div className="absolute inset-0 flex items-center justify-center text-center p-6">
                        <div>
                          <h3 className="text-2xl md:text-4xl font-display font-bold transition-colors duration-500 group-hover:text-primary">
                            {title}
                          </h3>
                          {desc && (
                            <div className="max-h-0 opacity-0 overflow-hidden transition-all duration-700 ease-out group-hover:max-h-40 group-hover:opacity-100">
                              <p className="text-muted-foreground text-sm md:text-base leading-relaxed max-w-xl mt-2">
                                {desc}
                              </p>
                            </div>
                          )}
                        </div>
                      </div>
                    </Link>
                  </motion.div>
                );
              })}
            </div>
            <Link to="/portfolio" className="block text-center text-muted-foreground hover:text-primary text-sm font-display tracking-wider uppercase mt-10 transition-colors">
              {t('Показать ещё', 'Show more')}
            </Link>
          </>
        ) : (
          <>
            <div className="flex flex-col gap-6">
              {placeholders.map((item, i) => (
                <PlaceholderCard key={i} item={item} index={i} />
              ))}
            </div>
            <Link to="/portfolio" className="block text-center text-muted-foreground hover:text-primary text-sm font-display tracking-wider uppercase mt-10 transition-colors">
              {t('Показать ещё', 'Show more')}
            </Link>
          </>
        )}
      </div>
    </section>
  );
};

export default PortfolioGrid;
